import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Option } from '@spike/model';
import Button from 'components/UI/V3/Button/Button';
import { useAuth, useServiceCategories } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './AppointmentsExportCSVModal.module.scss';
import AppointmentsExportCSVModalItem from './AppointmentsExportCSVModalItem';
import { getExportServiceCategoryOptions } from './exportServiceCategories';

interface AppointmentsExportCSVModalProps {
    selectedMarketplaceIds?: Array<number>;
    onExport?: (selectedCategories: string[]) => void;
    onClose?: () => void;
}

const AppointmentsExportCSVModal: FunctionComponent<AppointmentsExportCSVModalProps> = props => {
    const auth = useAuth();
    const currentFlags = useServiceCategories();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const options = useMemo(
        () =>
            getExportServiceCategoryOptions(
                organizationMarketplaces,
                props.selectedMarketplaceIds ?? [],
                auth.marketplaceId,
                currentFlags
            ),
        [auth.marketplaceId, currentFlags, organizationMarketplaces, props.selectedMarketplaceIds]
    );

    const [selectedServiceCategories, setSelectedServiceCategories] = useState(options.map(option => option.id));

    const exportHandler = () => {
        props.onExport?.([...selectedServiceCategories]);
    };

    const clickHandler = (option: Option<string>, checked: boolean) => {
        setSelectedServiceCategories(prev => [...prev.filter(st => st !== option.id), ...(checked ? [option.id] : [])]);
    };

    return createPortal(
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.closeIconContainer}>
                        <FontAwesomeIcon
                            icon={faTimes}
                            fontSize={18}
                            onClick={props.onClose}
                            className={styles.closeIcon}
                        />
                    </div>
                    <p className={styles.title}>Export Bookings</p>
                    <p className={styles.description}>Please select the services categories you want to export.</p>
                </div>
                <div className={styles.body}>
                    {options.map(option => (
                        <AppointmentsExportCSVModalItem
                            key={option.id}
                            label={option.name}
                            selected={selectedServiceCategories.includes(option.id)}
                            onClick={selected => clickHandler(option, selected)}
                        />
                    ))}
                </div>
                <div className={styles.footer}>
                    <Button
                        variant="black"
                        label="Cancel"
                        fullWidth={true}
                        onClick={props.onClose}
                    />
                    <Button
                        variant="green"
                        label="Export"
                        fullWidth={true}
                        disabled={selectedServiceCategories.length === 0}
                        onClick={exportHandler}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AppointmentsExportCSVModal;
